import {
  BadgeCheck,
  ExternalLink,
  Link2,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';

type Platform = 'wordpress' | 'shopify';

type BridgeConfig = {
  platform: Platform;
  title: string;
  adminLabel: string;
  activeNav: string;
  siteName: string;
  installId: string;
  platformBadge: string;
  primaryColor: string;
  softColor: string;
  billing: string;
  storefront: string;
  runtime: string;
  primaryCta: string;
  ctaHint: string;
  checks: Array<{ label: string; detail: string }>;
};

const configs: Record<Platform, BridgeConfig> = {
  wordpress: {
    platform: 'wordpress',
    title: 'Reach for WordPress',
    adminLabel: 'WordPress admin',
    activeNav: 'Reach',
    siteName: 'tokeepo.com',
    installId: 'wp_tokeepo_7n42',
    platformBadge: 'WordPress plugin',
    primaryColor: '#2271b1',
    softColor: '#eff6ff',
    billing: 'Managed in Reach Console',
    storefront: 'Plugin loader active',
    runtime: 'Plugin version 1.4.2',
    primaryCta: 'Manage Reach in WordPress',
    ctaHint: 'Use this button to open the Reach management screen from wp-admin.',
    checks: [
      { label: 'Plugin installed', detail: 'Active in wp-admin' },
      { label: 'Site verified', detail: 'Matched against https://tokeepo.com' },
      { label: 'Widget visible', detail: 'Detected on production pages' },
    ],
  },
  shopify: {
    platform: 'shopify',
    title: 'Reach for Shopify',
    adminLabel: 'Shopify admin',
    activeNav: 'Apps',
    siteName: 'tokeepo.myshopify.com',
    installId: 'shop_tokeepo_c91x',
    platformBadge: 'Shopify embedded app',
    primaryColor: '#008060',
    softColor: '#f0fdf4',
    billing: 'Active through Shopify',
    storefront: 'Theme app embed enabled',
    runtime: 'App Bridge session verified',
    primaryCta: 'Manage Reach in Shopify',
    ctaHint: 'Use this button to open the embedded Reach app inside Shopify admin.',
    checks: [
      { label: 'App installed', detail: 'OAuth token stored server-side' },
      { label: 'Shop verified', detail: 'Admin session validated' },
      { label: 'Billing active', detail: 'Growth plan approved in Shopify' },
    ],
  },
};

const navItems = ['Home', 'Orders', 'Products', 'Customers', 'Apps', 'Settings', 'Reach', 'Tools'];

export function WordPressBridgeMockup() {
  return <PlatformBridgeMockup config={configs.wordpress} />;
}

export function ShopifyBridgeMockup() {
  return <PlatformBridgeMockup config={configs.shopify} />;
}

export function WordPressUnlinkedBridgeMockup() {
  return <PlatformBridgeMockup config={configs.wordpress} state="unlinked" />;
}

export function ShopifyUnlinkedBridgeMockup() {
  return <PlatformBridgeMockup config={configs.shopify} state="unlinked" />;
}

function PlatformBridgeMockup({
  config,
  state = 'linked',
}: {
  config: BridgeConfig;
  state?: 'linked' | 'unlinked';
}) {
  const isLinked = state === 'linked';
  const statusLabel = isLinked ? 'Connected and healthy' : 'Reach is not linked yet';
  const headline = isLinked ? config.siteName : 'Connect this store to Reach';
  const summary = isLinked
    ? 'Reach is connected and ready. Most setup details are handled automatically, so this page only needs to confirm the connection and point admins to the management screen.'
    : 'This is the first screen a merchant sees after opening Reach from the platform admin. The page keeps setup focused on one action: link the current store, then continue managing Reach.';
  const primaryCta = isLinked ? config.primaryCta : `Link ${config.platform === 'wordpress' ? 'WordPress site' : 'Shopify store'} to Reach`;
  const ctaEyebrow = isLinked ? 'Manage app' : 'First step';
  const ctaTitle = isLinked ? 'Continue in Reach' : 'Link this store';
  const ctaDescription = isLinked
    ? `${config.ctaHint} Widget styling, support channels, assistant behavior, and plan settings live there.`
    : `Use this button to create the Reach connection for ${config.siteName}. After linking, this same page becomes the management entry point.`;
  const statusChecks = isLinked
    ? config.checks
    : [
        {
          label: config.platform === 'wordpress' ? 'Plugin detected' : 'App opened in admin',
          detail: config.platform === 'wordpress' ? 'Reach is active in wp-admin' : 'Shopify session is available',
        },
        { label: 'Store ready to link', detail: config.siteName },
        { label: 'No widget published yet', detail: 'Link the store before enabling storefront controls' },
      ];

  return (
    <div className="min-h-screen bg-[#f6f7f7] text-[#1d2327]">
      <div className="grid min-h-screen grid-cols-[232px_minmax(0,1fr)] max-lg:grid-cols-1">
        <aside className="border-r border-black/10 bg-[#f6f7f7] px-3 py-5 max-lg:hidden">
          <div className="mb-4 px-3 text-xs font-bold uppercase tracking-normal text-zinc-500">
            {config.adminLabel}
          </div>
          <nav className="space-y-1">
            {navItems.map((item) => {
              const isActive = item === config.activeNav;
              return (
                <div
                  key={item}
                  className={[
                    'flex h-9 items-center rounded-md px-3 text-sm font-medium',
                    isActive
                      ? 'border border-black/10 bg-white text-zinc-950 shadow-sm'
                      : 'text-zinc-500',
                  ].join(' ')}
                >
                  {item}
                </div>
              );
            })}
          </nav>
        </aside>

        <main className="px-7 py-7 max-sm:px-4">
          <div className="mb-5 flex items-start justify-between gap-5 max-md:flex-col">
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-normal text-zinc-500">
                {config.platformBadge}
              </p>
              <h1 className="text-3xl font-semibold tracking-normal text-zinc-950">{config.title}</h1>
            </div>
            <div className="flex flex-wrap gap-2">
              {isLinked && (
                <button className="inline-flex h-10 items-center gap-2 rounded-md border border-black/15 bg-white px-4 text-sm font-semibold text-zinc-700">
                  <RefreshCw className="h-4 w-4" />
                  Sync
                </button>
              )}
              <PrimaryAction color={config.primaryColor} label={primaryCta} icon={isLinked ? 'external' : 'link'} />
            </div>
          </div>

          <section className="mb-4 rounded-lg border border-black/10 bg-white p-6 shadow-[0_16px_40px_rgba(29,35,39,0.08)]">
            <div className="grid grid-cols-[minmax(0,1fr)_360px] gap-6 max-lg:grid-cols-1">
              <div className="min-w-0">
                <div
                  className="mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-bold"
                  style={{
                    backgroundColor: config.softColor,
                    borderColor: `${config.primaryColor}33`,
                    color: config.primaryColor,
                  }}
                >
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: isLinked ? config.primaryColor : '#d97706' }}
                  />
                  {statusLabel}
                </div>
                <h2 className="mb-1 text-2xl font-semibold tracking-normal text-zinc-950">
                  {headline}
                </h2>
                <p className="max-w-2xl text-sm leading-6 text-zinc-600">
                  {summary}
                </p>
              </div>

              <div className="grid gap-2">
                <Fact label={config.platform === 'wordpress' ? 'Site' : 'Store'} value={config.siteName} />
                <Fact label="Storefront" value={isLinked ? config.storefront : 'Not enabled yet'} />
              </div>
            </div>
          </section>

          <section className="grid grid-cols-[minmax(0,0.82fr)_minmax(360px,1fr)] gap-4 max-lg:grid-cols-1">
            <Panel>
              <PanelHeader
                eyebrow={isLinked ? 'Connection check' : 'Before linking'}
                title={isLinked ? 'Everything needed is active' : 'Ready to create connection'}
              />
              <div className="grid gap-3">
                {statusChecks.map((check) => (
                  <div key={check.label} className="grid grid-cols-[32px_1fr] rounded-md border border-black/10 bg-zinc-50 p-3">
                    <span
                      className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full"
                      style={{ backgroundColor: config.softColor, color: config.primaryColor }}
                    >
                      <BadgeCheck className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="font-semibold text-zinc-950">{check.label}</div>
                      <div className="mt-0.5 text-sm text-zinc-500">{check.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel className="relative overflow-hidden">
              <div
                className="absolute inset-x-0 top-0 h-1"
                style={{ backgroundColor: config.primaryColor }}
              />
              <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-full" style={{ backgroundColor: config.softColor, color: config.primaryColor }}>
                {isLinked ? <ShieldCheck className="h-5 w-5" /> : <Link2 className="h-5 w-5" />}
              </div>
              <p className="mb-1 text-xs font-bold uppercase tracking-normal text-zinc-500">{ctaEyebrow}</p>
              <h3 className="mb-2 text-2xl font-semibold tracking-normal text-zinc-950">
                {ctaTitle}
              </h3>
              <p className="mb-5 max-w-xl text-sm leading-6 text-zinc-600">
                {ctaDescription}
              </p>
              <PrimaryAction color={config.primaryColor} label={primaryCta} icon={isLinked ? 'external' : 'link'} large />
              <div className="mt-5 rounded-md border border-black/10 bg-zinc-50 p-3">
                <div className="text-xs font-bold text-zinc-500">{isLinked ? 'Installation' : 'What happens next'}</div>
                <div className="mt-1 text-sm font-semibold leading-5 text-zinc-950">
                  {isLinked ? config.installId : 'Reach verifies the store and opens setup.'}
                </div>
              </div>
            </Panel>
          </section>
        </main>
      </div>
    </div>
  );
}

function PrimaryAction({
  color,
  label,
  icon,
  large = false,
}: {
  color: string;
  label: string;
  icon: 'external' | 'link';
  large?: boolean;
}) {
  const Icon = icon === 'external' ? ExternalLink : Link2;
  return (
    <button
      className={[
        'inline-flex items-center gap-2 rounded-md px-5 text-sm font-semibold text-white shadow-sm',
        large ? 'h-11' : 'h-10',
      ].join(' ')}
      style={{ backgroundColor: color }}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-black/10 bg-zinc-50 p-3">
      <div className="text-xs font-bold text-zinc-500">{label}</div>
      <div className="mt-1 text-sm font-semibold leading-5 text-zinc-950">{value}</div>
    </div>
  );
}

function Panel({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <article className={`rounded-lg border border-black/10 bg-white p-5 ${className}`}>
      {children}
    </article>
  );
}

function PanelHeader({
  eyebrow,
  title,
  action,
}: {
  eyebrow: string;
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        <p className="mb-1 text-xs font-bold uppercase tracking-normal text-zinc-500">{eyebrow}</p>
        <h3 className="text-lg font-semibold tracking-normal text-zinc-950">{title}</h3>
      </div>
      {action}
    </div>
  );
}
